/**
 * The Leaflet icon of one twin marker. The shape is the twin's location mode
 * (a circle, hexagon or diamond: colour-blind redundancy with a list chip), the
 * colours are the caller's, so a map can encode the twin's model in them. A
 * selected marker gets a pulsing ring, a valid pick target a green one, and a
 * name pill can sit under the marker.
 *
 * The rings bring their own pulse: the first icon with a ring adds its
 * `@keyframes` to the page (`ensureMarkerPulseKeyframes`).
 */
import L from 'leaflet';
import {
  MARKER_PULSE,
  MARKER_SELECTION,
  ensureMarkerPulseKeyframes,
  escapeHtml,
  markerInnerHtml,
  safeMarkerThumbnailUrl,
  type LocationModeTag,
} from '../markerIcons';

/** How a marker shows a relationship pick in progress: a valid target rings green, the rest dim. */
export type MarkerPick = 'valid' | 'invalid';

/** A marker's fill and its outline. */
export interface MarkerColors {
  fill: string;
  stroke: string;
}

/** The marker colours when the host gives none. */
export const MARKER_COLORS: MarkerColors = { fill: '#1976d2', stroke: '#1565c0' };

/** What the marker icon reads of a twin. */
export interface MarkerIconEntry {
  twinId: string;
  locationMode: LocationModeTag;
  /** An image of the twin's model: the marker is drawn larger, filled with it. An unsafe URL is ignored (`safeMarkerThumbnailUrl`). */
  thumbnailUrl?: string;
}

/** How the marker shows: selected, a pick in progress, a name pill under it. */
export interface MarkerIconState {
  selected?: boolean;
  pick?: MarkerPick;
  /** Text of a name pill under the marker. */
  label?: string;
}

// The ring of a valid pick target.
const PICK_VALID = '#4caf50';

/** The `L.divIcon` of a twin's marker, anchored on its centre. */
export function makeMarkerIcon(
  entry: MarkerIconEntry,
  colors: MarkerColors,
  { selected = false, pick, label }: MarkerIconState = {},
): L.DivIcon {
  // Markers carrying a thumbnail render larger so the image stays legible.
  // The URL is validated before it reaches the inline-style HTML sink; an
  // unsafe URL falls back to the plain coloured shape at the smaller size.
  const thumbnailUrl = safeMarkerThumbnailUrl(entry.thumbnailUrl);
  const size = thumbnailUrl ? (selected ? 40 : 30) : selected ? 22 : 16;
  const ring = (
    color: string,
    inset: number,
    anim = `animation:${MARKER_PULSE} 1.4s ease-out infinite;opacity:0.65`,
  ) =>
    `<div style="position:absolute;inset:-${inset}px;border-radius:50%;border:2px solid ${color};${anim}"></div>`;
  // A valid pick target gets a steady ring under the pulse: the pulse alone fades out between beats.
  const pulse = selected
    ? ring(MARKER_SELECTION.selected, 4)
    : pick === 'valid'
      ? ring(PICK_VALID, 4, '') + ring(PICK_VALID, 6)
      : '';
  if (pulse) ensureMarkerPulseKeyframes();
  const dim = pick === 'invalid' ? ';opacity:0.25' : '';
  const inner = markerInnerHtml({
    mode: entry.locationMode,
    size,
    fill: colors.fill,
    stroke: colors.stroke,
    isSelected: selected,
    thumbnailUrl,
  });
  // tooltipAnchor: bumped from -size/2 to -size*0.7 so the diamond's rotated
  // top tip (which extends ~size*0.354 above its bounding-box top) doesn't
  // overlap the tooltip arrow.
  // data-tt-* name the twin and its selected state, for scripts and tests that drive the map
  // (escapeHtml also escapes quotes, so it is safe in a double-quoted attribute).
  const attrs = `data-tt-twin="${escapeHtml(entry.twinId)}"${selected ? ' data-tt-selected="true"' : ''}`;
  const pill = label
    ? `<div style="position:absolute;top:calc(100% + 3px);left:50%;transform:translateX(-50%);white-space:nowrap;pointer-events:none;font-size:11px;line-height:1.3;padding:0 5px;border-radius:8px;background:rgba(0,0,0,0.65);color:#fff">${escapeHtml(label)}</div>`
    : '';
  return L.divIcon({
    className: '',
    html: `<div ${attrs} style="position:relative;width:${size}px;height:${size}px${dim}">${pulse}${inner}${pill}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    tooltipAnchor: [0, -size * 0.7],
  });
}
